import { Vanchi, VanchiStats, VanchiStatus } from '../types';
import { apiClient } from '../common/utils/apiClient';
import { API_CONFIG } from '../config/api';
import { sessionStorageUtils } from '../common/utils/sessionStorage';


// Real vanchi service
export const vanchiService = {
  getVanchiList: async (): Promise<any[]> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    return apiClient.get<any[]>(API_CONFIG.ENDPOINTS.PACKLOG, token);
  },

  getVanchiStats: async (): Promise<VanchiStats> => {
    // Compute stats by filtering on status, across COMPLETE dataset
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');

    // Try to fetch all pages using page/limit if the backend supports it
    const pageSize = 500;
    let page = 1;
    let all: any[] = [];
    // First request
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    let resp: any = await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.PACKLOG}?page=${page}&limit=${pageSize}`, token);
    if (Array.isArray(resp)) {
      // Backend returned full array without pagination
      all = resp;
    } else {
      const total = resp?.rowCount ?? resp?.total ?? (resp?.data?.length || 0);
      all = resp?.data ?? [];
      const totalPages = total && pageSize ? Math.ceil(total / pageSize) : 1;
      while (page < totalPages) {
        page += 1;
        const next = await apiClient.get<any>(`${API_CONFIG.ENDPOINTS.PACKLOG}?page=${page}&limit=${pageSize}`, token);
        const chunk = Array.isArray(next) ? next : (next?.data ?? []);
        all = all.concat(chunk);
        if (!chunk.length) break;
      }
    }
    const list: any[] = all;
    const toDate = (s?: string) => (s ? new Date(s) : null);
    const sameDay = (d: Date, ref: Date) => d.toDateString() === ref.toDateString();
    const now = new Date();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const startOfWeek = new Date(now); startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    const inRange = (d: Date | null, start: Date, end: Date) => !!d && d >= start && d <= end;
    const isReceived = (x: any) => String(x.packstatus || '').trim().toLowerCase() === 'received';
    const isPackedSent = (x: any) => {
      const k = String(x.packstatus || '').trim().toLowerCase();
      return k === 'in_transit' || k === 'packed' || k === 'sealed' || k === 'sent';
    };

    // today totals by date window, then derive packed = total - received
    const todayTotal = list.filter(x => {
      const d = toDate(x.packdt) || toDate(x.receivedt);
      return d && sameDay(d, now);
    }).length;
    const todayReceived = list.filter(x => {
      const d = toDate(x.receivedt);
      return d && sameDay(d, now) && isReceived(x);
    }).length;
    const todayPacked = Math.max(0, todayTotal - todayReceived);

    // yesterday
    const yTotal = list.filter(x => {
      const d = toDate(x.packdt) || toDate(x.receivedt);
      return d && sameDay(d, yesterday);
    }).length;
    const yReceived = list.filter(x => {
      const d = toDate(x.receivedt);
      return d && sameDay(d, yesterday) && isReceived(x);
    }).length;
    const yPacked = Math.max(0, yTotal - yReceived);

    return {
      today: { packedSent: todayPacked, received: todayReceived },
      yesterday: { packedSent: yPacked, received: yReceived },
      week: {
        received: list.filter(x => inRange(toDate(x.receivedt), startOfWeek, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          list.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfWeek, now)).length -
          list.filter(x => inRange(toDate(x.receivedt), startOfWeek, now) && isReceived(x)).length
        ),
      },
      month: {
        received: list.filter(x => inRange(toDate(x.receivedt), startOfMonth, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          list.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfMonth, now)).length -
          list.filter(x => inRange(toDate(x.receivedt), startOfMonth, now) && isReceived(x)).length
        ),
      },
      year: {
        received: list.filter(x => inRange(toDate(x.receivedt), startOfYear, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          list.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfYear, now)).length -
          list.filter(x => inRange(toDate(x.receivedt), startOfYear, now) && isReceived(x)).length
        ),
      },
    };
  },


  getVanchiByDateRange: async (startDate: string, endDate: string): Promise<any[]> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    return apiClient.get<any[]>(API_CONFIG.ENDPOINTS.PACKLOG_BY_DATE(startDate, endDate), token);
  },

  getReportStats: async (startDate: string, endDate: string): Promise<VanchiStats> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');

    // Fetch packlog data for the date range
    const data = await apiClient.get<any[]>(API_CONFIG.ENDPOINTS.PACKLOG_BY_DATE(startDate, endDate), token);
    const list: any[] = Array.isArray(data) ? data : (data?.data || []);

    const toDate = (s?: string) => (s ? new Date(s) : null);
    const sameDay = (d: Date, ref: Date) => d.toDateString() === ref.toDateString();
    const now = new Date();
    const yesterday = new Date(now); 
    yesterday.setDate(now.getDate() - 1);
    const startOfWeek = new Date(now); 
    startOfWeek.setDate(now.getDate() - now.getDay());
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    const inRange = (d: Date | null, start: Date, end: Date) => !!d && d >= start && d <= end;
    const isReceived = (x: any) => String(x.packstatus || '').trim().toLowerCase() === 'received';
    const isPackedSent = (x: any) => {
      const k = String(x.packstatus || '').trim().toLowerCase();
      return k === 'in_transit' || k === 'packed' || k === 'sealed' || k === 'sent';
    };

    // Calculate stats for the date range
    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);

    // Filter data within the date range
    const rangeData = list.filter(x => {
      const packDate = toDate(x.packdt);
      const receiveDate = toDate(x.receivedt);
      return (packDate && inRange(packDate, rangeStart, rangeEnd)) || 
             (receiveDate && inRange(receiveDate, rangeStart, rangeEnd));
    });

    // Calculate stats for different periods within the range
    const todayTotal = rangeData.filter(x => {
      const d = toDate(x.packdt) || toDate(x.receivedt);
      return d && sameDay(d, now);
    }).length;
    const todayReceived = rangeData.filter(x => {
      const d = toDate(x.receivedt);
      return d && sameDay(d, now) && isReceived(x);
    }).length;
    const todayPacked = Math.max(0, todayTotal - todayReceived);

    // Yesterday
    const yTotal = rangeData.filter(x => {
      const d = toDate(x.packdt) || toDate(x.receivedt);
      return d && sameDay(d, yesterday);
    }).length;
    const yReceived = rangeData.filter(x => {
      const d = toDate(x.receivedt);
      return d && sameDay(d, yesterday) && isReceived(x);
    }).length;
    const yPacked = Math.max(0, yTotal - yReceived);

    return {
      today: { packedSent: todayPacked, received: todayReceived },
      yesterday: { packedSent: yPacked, received: yReceived },
      week: {
        received: rangeData.filter(x => inRange(toDate(x.receivedt), startOfWeek, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          rangeData.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfWeek, now)).length -
          rangeData.filter(x => inRange(toDate(x.receivedt), startOfWeek, now) && isReceived(x)).length
        ),
      },
      month: {
        received: rangeData.filter(x => inRange(toDate(x.receivedt), startOfMonth, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          rangeData.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfMonth, now)).length -
          rangeData.filter(x => inRange(toDate(x.receivedt), startOfMonth, now) && isReceived(x)).length
        ),
      },
      year: {
        received: rangeData.filter(x => inRange(toDate(x.receivedt), startOfYear, now) && isReceived(x)).length,
        packedSent: Math.max(0,
          rangeData.filter(x => inRange(toDate(x.packdt) || toDate(x.receivedt), startOfYear, now)).length -
          rangeData.filter(x => inRange(toDate(x.receivedt), startOfYear, now) && isReceived(x)).length
        ),
      },
    };
  },
};
